import { useState, useEffect } from "react";
import { toast } from "sonner";
import AdminPortal from "@/components/dashboard/AdminPortal";
import RelocationDesk from "@/components/dashboard/RelocationDesk";
import FoodFunDesk from "@/components/dashboard/FoodFunDesk";
import VanDesignWorkspace from "@/components/vandesign/VanDesignWorkspace";
import AdMakerWorkspace from "@/components/mist/AdMakerWorkspace";
import MarketingWorkspace from "@/components/workspaces/MarketingWorkspace";
import {
  ArrowLeft, ChevronRight, ShieldCheck, Wand2, ClipboardList, Columns, Send,
  Sparkles, Plane, UtensilsCrossed, HeartPulse, Home, TrendingUp, SlidersHorizontal,
  Palette, Shirt, ShoppingBag, BookOpen, FileText, Truck, Crown, Settings, BarChart3,
  Shield, Wallet, Landmark, Receipt, Coins, Gamepad2, Globe, Newspaper, Users, Bell,
  Image, Images, Scissors, Clapperboard, Megaphone, Play, Music, PenLine, Radio, Mic,
  Feather, Type, Languages, Flame, Gift, Trophy, Target, MessageCircle, Bitcoin,
} from "lucide-react";

/* Full-screen hub landing pages — row-card lists (icon + title + description +
   chevron), one per legacy Explore MiSt hub. Each row is a labelled placeholder
   for now (tap = "coming soon"); wired to real engines in later batches. */
export const HUB_DETAIL = {
  social_hub: {
    title: "Social", subtitle: "Feeds · posts · community", accent: "#4EA8F0",
    rows: [
      { t: "Global Feed", d: "Discover posts from every creator", Icon: Globe },
      { t: "My Posts", d: "Manage your published content", Icon: Newspaper },
      { t: "Creators to Follow", d: "Find & connect with the network", Icon: Users },
      { t: "Notifications", d: "Requests · mentions · activity", Icon: Bell },
    ],
  },
  imagery_hub: {
    title: "AI Imagery", subtitle: "Generate · edit · style", accent: "#B06FE6",
    rows: [
      { t: "Image Generator", d: "Text-to-image with Nano Banana", Icon: Image },
      { t: "Style Transfer", d: "Restyle any photo in one tap", Icon: Palette },
      { t: "Photo Pack", d: "Batch generate a themed set", Icon: Images },
      { t: "Background Remover", d: "Clean cut-outs for products", Icon: Scissors },
    ],
  },
  video_hub: {
    title: "AI Video", subtitle: "Reels · welds · animation", accent: "#F2683C",
    rows: [
      { t: "Reel Studio", d: "Build vertical 1080×1920 reels", Icon: Clapperboard },
      { t: "Ad Pipeline", d: "Auto-weld intro + body ad clips", Icon: Megaphone },
      { t: "Image Animator", d: "Bring a still image to motion", Icon: Play },
      { t: "Lyric Reel", d: "Sync lyrics onto a track reel", Icon: Music },
    ],
  },
  music_hub: {
    title: "AI Music", subtitle: "Tracks · lyrics · sync", accent: "#5B6EF0",
    rows: [
      { t: "Song Generator", d: "Compose from a text prompt", Icon: Music },
      { t: "Lyric Writer", d: "AI-crafted lyrics in any style", Icon: PenLine },
      { t: "Lo-Fi Beats", d: "Chill background loops", Icon: Radio },
      { t: "Voice Memo", d: "Narration & voiceover tracks", Icon: Mic },
    ],
  },
  writing_hub: {
    title: "AI Writing", subtitle: "Scripts · copy · notes", accent: "#9B6BF0",
    rows: [
      { t: "Script Writer", d: "Reel & video scripts, fast", Icon: PenLine },
      { t: "Article Draft", d: "Long-form drafts & blogs", Icon: FileText },
      { t: "Poetry Maker", d: "Creative verse on demand", Icon: Feather },
      { t: "Caption Studio", d: "Hooky captions & hashtags", Icon: Type },
      { t: "Translator", d: "Multi-language rewriting", Icon: Languages },
    ],
  },
  career_hub: {
    title: "Career Hub", subtitle: "Your job-hunt cockpit — resume, jobs & pipeline", accent: "#1D4ED8",
    rows: [
      { t: "Career Vault", d: "Details, resume, history, licences, referees", Icon: ShieldCheck, color: "#1D4ED8" },
      { t: "Master Generator", d: "One-tap master resume + cover letter", Icon: Wand2, color: "#A78BFA" },
      { t: "Job Feed", d: "Add jobs · tailor for each role", Icon: ClipboardList, color: "#2563EB" },
      { t: "Pipeline Tracker", d: "Applied → Interviewing → Offer", Icon: Columns, color: "#22C55E" },
      { t: "Outreach Writer", d: "Cold LinkedIn / recruiter intros", Icon: Send, color: "#F97316" },
      { t: "More AI Tools", d: "Career match · compass · job-fit", Icon: Sparkles, color: "#64748B" },
    ],
  },
  wealth_hub: {
    title: "Wealth & Money", subtitle: "Assets · ledgers · fintech", accent: "#34D399",
    rows: [
      { t: "Wealth", d: "Asset tracking & net-worth canvas", Icon: Wallet },
      { t: "Finance", d: "Australian Basiq open-banking feed", Icon: Landmark },
      { t: "Tax", d: "Parsed receipt ingestion ledger", Icon: Receipt, routeHub: "tax_books" },
      { t: "Crypto Coach", d: "AI-driven financial advisory", Icon: Coins },
      { t: "Crypto Tax", d: "On-chain accounting & wallet metrics", Icon: FileText },
      { t: "Money Quest", d: "Gamified milestone trackers", Icon: Gamepad2 },
      { t: "Mingle", d: "Social money circles & pooled goals", Icon: MessageCircle },
      { t: "MILINK", d: "In-network MiLink token wallet & rewards", Icon: Bitcoin },
    ],
  },
  lifestyle_hub: {
    title: "Lifestyle", subtitle: "Travel · food · health · home", accent: "#38BDF8",
    rows: [
      { t: "Travel", d: "Trips, itineraries & relocation paths", Icon: Plane, page: "relocation" },
      { t: "Food & Fun", d: "Pivot & Save multi-stop finder", Icon: UtensilsCrossed, page: "foodfun" },
      { t: "Health & Fitness", d: "Intake, workouts & wellness", Icon: HeartPulse },
      { t: "Housing", d: "AU listings, relocation & spaces", Icon: Home, page: "relocation" },
    ],
  },
  rewards_hub: {
    title: "Grow & Rewards", subtitle: "Streaks · spins · milestones", accent: "#F2C94C",
    rows: [
      { t: "Daily Streak", d: "Keep your activity chain alive", Icon: Flame },
      { t: "Milestone Wheel", d: "One free spin per day", Icon: Gift },
      { t: "M-Token Sink", d: "Spend tokens for real perks", Icon: ShoppingBag },
      { t: "Leaderboard", d: "See how you rank", Icon: Trophy },
      { t: "Goals", d: "Set & advance growth goals", Icon: Target },
    ],
  },
  business_hub: {
    title: "Business & Tools", subtitle: "Promote · brand · utilities", accent: "#4F46E5",
    tabs: ["Promote", "Brand", "Utilities"],
    rows: [
      { t: "Marketing", d: "Auto-generate high-converting 9:16 social ads with AI", Icon: TrendingUp, cat: "Promote", page: "marketing" },
      { t: "Social Kit", d: "Platform managers · content calendars · assets matching", Icon: SlidersHorizontal, cat: "Promote" },
      { t: "Merch Studio", d: "Design custom apparel · print-on-demand links · collective fulfilment gates", Icon: Shirt, cat: "Brand" },
      { t: "Store", d: "E-commerce portals · product checkout trackers", Icon: ShoppingBag, cat: "Promote" },
      { t: "Translator", d: "Global text multi-language translation pipelines", Icon: BookOpen, cat: "Utilities" },
      { t: "Meeting Notes", d: "Interactive logs · action items · team shared storage", Icon: FileText, cat: "Utilities" },
      { t: "Van Life", d: "AI van fit-out layouts · blueprints · 3D walkthrough", Icon: Truck, cat: "Utilities", page: "vandesign" },
      { t: "Creator Pack", d: "Pro creator toolkit · monetization perks · priority render lane", Icon: Crown, cat: "Brand" },
    ],
  },
  account_hub: {
    title: "Account Hub", subtitle: "Profile · settings · admin", accent: "#F2C94C",
    rows: [
      { t: "SETTINGS", d: "Preferences & configuration", Icon: Settings },
      { t: "SECURITY", d: "PIN · biometric · 2FA", Icon: Shield },
      { t: "MY DRAFTS", d: "Saved & in-progress creations", Icon: FileText },
      { t: "ANALYTICS", d: "Your performance metrics", Icon: BarChart3 },
      { t: "EARN", d: "Creator payouts & revenue", Icon: TrendingUp },
      { t: "ADMIN PORTAL", d: "System-wide command center", Icon: Shield, page: "admin" },
      { t: "ENGINE STUDIO", d: "Model & pipeline configuration", Icon: Sparkles },
    ],
  },
};

// Aliases — the 4 Explore MiSt master macros map to the verified hub specs.
HUB_DETAIL.wealth_money = HUB_DETAIL.wealth_hub;
HUB_DETAIL.business_tools = HUB_DETAIL.business_hub;
HUB_DETAIL.career = HUB_DETAIL.career_hub;
HUB_DETAIL.lifestyle_leisure = HUB_DETAIL.lifestyle_hub;

export default function HubDetail({ hub, onBack }) {
  const [subPage, setSubPage] = useState(null);
  const [activeTab, setActiveTab] = useState("All");
  // Deep-link: ?sub=board auto-descends the Travel hub straight into the
  // Relocation Board (shareable trip-board links). On mount + popstate.
  useEffect(() => {
    if (hub.hubKey !== "lifestyle_hub") return;
    const check = () => {
      if (new URLSearchParams(window.location.search).get("sub") === "board") setSubPage("relocation");
    };
    check();
    window.addEventListener("popstate", check);
    return () => window.removeEventListener("popstate", check);
  }, [hub.hubKey]);
  const detail = HUB_DETAIL[hub.hubKey] || { title: hub.title, subtitle: "", accent: hub.bg, rows: [] };
  const accent = detail.accent || hub.bg || "#6C63FF";
  const tabs = detail.tabs ? ["All", ...detail.tabs] : null;
  const rows = tabs && activeTab !== "All"
    ? detail.rows.filter((r) => r.cat === activeTab)
    : detail.rows;

  if (subPage === "admin") return <AdminPortal onBack={() => setSubPage(null)} />;
  if (subPage === "relocation") return <RelocationDesk onBack={() => setSubPage(null)} />;
  if (subPage === "foodfun") return <FoodFunDesk onBack={() => setSubPage(null)} />;
  if (subPage === "vandesign") return <VanDesignWorkspace onBack={() => setSubPage(null)} />;
  if (subPage === "admaker") return <AdMakerWorkspace onBack={() => setSubPage(null)} />;
  if (subPage === "marketing") return <MarketingWorkspace onClose={() => setSubPage(null)} />;

  const onRow = (r) => {
    if (r.page) setSubPage(r.page);
    else toast(`${r.t} — coming soon`);
  };

  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-cream" data-testid={`hub-detail-${hub.hubKey}`}>
      <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3 bg-cream border-b-2 border-charcoal/10">
        <button onClick={onBack} data-testid="hub-detail-back" aria-label="Back" className="w-10 h-10 rounded-full bg-white border-2 border-charcoal grid place-items-center shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><ArrowLeft size={18} strokeWidth={2.4} /></button>
        <div className="leading-tight min-w-0">
          <h1 className="font-serif text-2xl text-charcoal truncate">{detail.title}</h1>
          {detail.subtitle && <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-charcoal/50 truncate">{detail.subtitle}</p>}
        </div>
      </header>

      {tabs && (
        <div className="shrink-0 px-4 pt-3" data-testid={`hub-tabs-${hub.hubKey}`}>
          <div className="grid gap-1.5 bg-white border-2 border-charcoal rounded-2xl p-1 shadow-[3px_3px_0px_#1A1A1A]" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
            {tabs.map((t) => (
              <button key={t} onClick={() => setActiveTab(t)} data-testid={`hub-tab-${hub.hubKey}-${t.toLowerCase()}`}
                className={`font-mono text-[9px] font-black uppercase tracking-widest rounded-xl py-2 transition-colors ${activeTab === t ? "bg-charcoal text-white" : "text-charcoal/50"}`}>
                {t}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pt-4 pb-28 space-y-3">
        {rows.map((r, i) => {
          const Icon = r.Icon;
          return (
            <button key={`${r.t}-${i}`} onClick={() => onRow(r)} data-testid={`hub-row-${hub.hubKey}-${detail.rows.indexOf(r)}`}
              className="w-full flex items-center gap-3 border-2 border-charcoal rounded-2xl bg-white px-3.5 py-3 shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-transform text-left">
              <span className="w-11 h-11 shrink-0 rounded-xl border-2 border-charcoal grid place-items-center" style={{ backgroundColor: r.color || accent }}>
                <Icon size={20} strokeWidth={2.3} className="text-white" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-sans font-bold text-[15px] text-charcoal leading-tight flex items-center gap-1.5">
                  {r.t}
                  {!r.page && !r.routeHub && (
                    <span className="font-mono text-[10px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-charcoal/10 text-charcoal/55 border border-charcoal/20" data-testid={`hub-soon-${detail.rows.indexOf(r)}`}>Soon</span>
                  )}
                </p>
                <p className="font-mono text-xs text-charcoal/60 leading-snug mt-0.5">{r.d}</p>
              </div>
              <ChevronRight size={20} className="text-charcoal/40 shrink-0" />
            </button>
          );
        })}
      </div>
    </div>
  );
}
